import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import {
  FailureSvg,
  PendingSvg,
  RoundedCrossSvg,
  SuccessSvg,
} from '@cashier/cashier-assets';
import { FinalStates, ResultMessageTypes } from '@cashier/cashier-model';
import { useUIConfiguration } from '@cashier/cashier-shared';
import { Button, cn, PlainHtmlContainer } from '@cashier/cashier-ui';

import { useStatusBlock } from '../useStatusBlock';

import styles from './styles.module.scss';

interface StatusBlockItem {
  icon: ReactElement;
  title: string;
  buttonText: string;
}

interface StatusBlockProps {
  noVerticalPadding?: boolean;
  onButtonClick: () => void;
}

export const StatusBlock = ({
  onButtonClick,
  noVerticalPadding = false,
}: StatusBlockProps): ReactElement => {
  const { t } = useTranslation();
  const buttonBackground = useUIConfiguration('button', 'background');
  const buttonType = useUIConfiguration('button', 'type');

  const { finalState, message } = useStatusBlock();
  const isRestrictedContainer =
    noVerticalPadding && FinalStates.RESTRICTED === finalState;

  const statusBlockConfiguration: Record<FinalStates, StatusBlockItem> = {
    [FinalStates.SUCCESS]: {
      icon: <SuccessSvg />,
      title: t('thankYou.heading'),
      buttonText: t('close.button'),
    },
    [FinalStates.ERROR]: {
      icon: <FailureSvg />,
      title: t('finalScreenFailure.title'),
      buttonText: t('retry.button'),
    },
    [FinalStates.PAYMENT_METHOD_ERROR]: {
      icon: <FailureSvg />,
      title: t('finalScreenFailure.title'),
      buttonText: t('close.button'),
    },
    [FinalStates.PENDING]: {
      icon: <PendingSvg />,
      title: t('pendingTransaction.message'),
      buttonText: t('makeNewTransaction.button'),
    },
    [FinalStates.RESTRICTED]: {
      // noVerticalPadding is true when the submitInitiator = Parent
      icon: noVerticalPadding ? <RoundedCrossSvg /> : <PendingSvg />,
      title: message?.content || '',
      buttonText: t('backToSelectMethod.button'),
    },
  };

  return (
    <div
      className={cn({
        [styles.container]: true,
        [styles.noVerticalPadding]: noVerticalPadding,
        [styles.restrictedContainer]: isRestrictedContainer,
      })}
    >
      <div
        className={cn([
          styles.icon,
          styles[finalState],
          ...(noVerticalPadding && FinalStates.RESTRICTED === finalState
            ? [styles.crossRoundedIcon]
            : []),
        ])}
        data-test={`${finalState}-icon`}
      >
        {statusBlockConfiguration[finalState].icon}
      </div>
      <div className={styles.title} data-test="final-title">
        {statusBlockConfiguration[finalState].title}
      </div>
      {message && FinalStates.SUCCESS === finalState && (
        <>
          {ResultMessageTypes.HTML === message.type && (
            <PlainHtmlContainer
              data-test="success-message"
              className={styles.text}
              content={message.content}
              config={{
                ADD_ATTR: ['target'],
              }}
            />
          )}
          {ResultMessageTypes.STRING === message.type && (
            <div data-test="success-message" className={styles.text}>
              {message.content}
            </div>
          )}
        </>
      )}

      {message && FinalStates.ERROR === finalState && (
        <>
          {message.type === ResultMessageTypes.HTML && (
            <PlainHtmlContainer
              className={cn([styles.text, styles.error])}
              data-test="failure-message"
              content={message.content}
              config={{
                ADD_ATTR: ['target'],
              }}
            />
          )}
          {message.type === ResultMessageTypes.STRING && (
            <div
              className={cn([styles.text, styles.error])}
              data-test="failure-message"
            >
              {message.content}
            </div>
          )}
        </>
      )}

      {message && FinalStates.PAYMENT_METHOD_ERROR === finalState && (
        <div
          className={cn([styles.text, styles.error])}
          data-test="missing-message"
        >
          {message.content}
        </div>
      )}

      {FinalStates.PENDING === finalState && (
        <>
          {message && (
            <>
              {ResultMessageTypes.HTML === message.type && (
                <PlainHtmlContainer
                  data-test="pending-message"
                  className={styles.text}
                  content={message.content}
                  config={{
                    ADD_ATTR: ['target'],
                  }}
                />
              )}
              {ResultMessageTypes.STRING === message.type && (
                <div data-test="pending-message" className={styles.text}>
                  {message.content}
                </div>
              )}
            </>
          )}

          {!message && (
            <>
              <div
                className={cn([styles.text, styles.pending])}
                data-test="pending-text-1"
              >
                {t('finalScreenPending.text1')}
              </div>
              <div
                className={cn([styles.whiteText, styles.pending])}
                data-test="pending-text-2"
              >
                {t('finalScreenPending.text2')}
              </div>
            </>
          )}
        </>
      )}

      {!isRestrictedContainer && (
        <div className={styles.button} data-test="close-button">
          <Button
            onClick={onButtonClick}
            type={buttonType}
            color="red"
            backgroundType={buttonBackground}
          >
            {statusBlockConfiguration[finalState].buttonText}
          </Button>
        </div>
      )}
    </div>
  );
};
